import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowsClockwise, ChartLineUp, Lightning, Lock, ShieldCheck, ArrowUpRight, ArrowDownRight, Plus } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NewPickForm } from "./MarketsAdmin";

const PRODUCT_META = {
  stocks: {
    name: "Stock Picks",
    accent: "#F59E0B",
    icon: ChartLineUp,
    cta: "Stocks pack",
    requiredPack: "stocks_pack",
  },
  options: {
    name: "Option Picks",
    accent: "#A855F7",
    icon: Lightning,
    cta: "Options pack",
    requiredPack: "options_pack",
  },
};

const pct = (n) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);
const usd = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const signed = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  return `${v > 0 ? "+" : v < 0 ? "" : ""}${v.toFixed(2)}%`;
};
const dateFmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

function OutcomeBadge({ outcome }) {
  if (!outcome) return null;
  const map = {
    hit_t1: { label: "T1 HIT", bg: "#22C55E", color: "#000" },
    hit_t2: { label: "T2 HIT", bg: "#10B981", color: "#000" },
    stopped: { label: "STOPPED", bg: "#FF3B30", color: "#000" },
    closed_manual: { label: "CLOSED", bg: "#6B7280", color: "#fff" },
    expired: { label: "EXPIRED", bg: "#52525B", color: "#fff" },
  };
  const s = map[outcome] || { label: outcome.toUpperCase(), bg: "#52525B", color: "#fff" };
  return (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function OpenStockCard({ pick, accent, hasAccess }) {
  const longShort = (pick.direction || "long").toUpperCase();
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0F0F0F] border rounded-sm p-5 hover:bg-[#141414] transition-colors"
      style={{ borderColor: `${accent}55` }}
      data-testid={`open-stock-${pick.ticker}`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <Lock size={11} weight="fill" className="text-[#22C55E]" />
          <span className="text-[9px] uppercase tracking-[0.25em] font-bold text-[#22C55E]">OPEN</span>
          {longShort === "LONG" ? <ArrowUpRight size={12} className="text-[#22C55E]" /> : <ArrowDownRight size={12} className="text-[#FF3B30]" />}
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: accent }}>{longShort}</span>
        </div>
        <span className="text-[10px] text-zinc-500 font-mono-d">{dateFmt(pick.posted_at)}</span>
      </div>
      <div className="font-display text-4xl leading-none mb-3">{pick.ticker}</div>
      {hasAccess ? (
        <div className="space-y-1 text-sm font-mono-d">
          <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Entry</span><span className="font-bold">{usd(pick.entry_price)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Target 1</span><span className="text-[#22C55E]">{usd(pick.target_1)}</span></div>
          {pick.target_2 && <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Target 2</span><span className="text-[#22C55E]">{usd(pick.target_2)}</span></div>}
          <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Stop</span><span className="text-[#FF3B30]">{usd(pick.stop_loss)}</span></div>
        </div>
      ) : (
        <div className="bg-black/40 border border-white/10 rounded-sm p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Subscribe to see</div>
          <div className="text-[11px] text-zinc-400">Entry · 2 targets · Stop · Rationale</div>
        </div>
      )}
      {hasAccess && pick.rationale && (
        <div className="mt-4 p-3 bg-black/40 border-l-2 rounded-sm text-[12px] text-zinc-300 leading-relaxed" style={{ borderColor: accent }}>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Rationale</div>
          {pick.rationale}
        </div>
      )}
    </motion.div>
  );
}

function OpenOptionCard({ pick, accent, hasAccess }) {
  const typeLabel = (pick.option_type || "call").toUpperCase();
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0F0F0F] border rounded-sm p-5 hover:bg-[#141414] transition-colors"
      style={{ borderColor: `${accent}55` }}
      data-testid={`open-option-${pick.underlying}-${pick.strike}-${pick.option_type}`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <Lock size={11} weight="fill" className="text-[#22C55E]" />
          <span className="text-[9px] uppercase tracking-[0.25em] font-bold text-[#22C55E]">OPEN</span>
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: typeLabel === "CALL" ? "#22C55E" : "#FF3B30" }}>{typeLabel}</span>
        </div>
        <span className="text-[10px] text-zinc-500 font-mono-d">{dateFmt(pick.posted_at)}</span>
      </div>
      <div className="font-display text-3xl leading-none mb-1">{pick.underlying}</div>
      <div className="text-sm text-zinc-400 mb-3 font-mono-d">
        ${pick.strike} {typeLabel} <span className="text-zinc-600">· exp {pick.expiration}</span>
      </div>
      {hasAccess ? (
        <div className="space-y-1 text-sm font-mono-d">
          <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Premium</span><span className="font-bold">${pick.entry_price?.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Contracts</span><span>{pick.contracts || 1}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Target 1</span><span className="text-[#22C55E]">${pick.target_1?.toFixed(2)}</span></div>
          {pick.target_2 && <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Target 2</span><span className="text-[#22C55E]">${pick.target_2?.toFixed(2)}</span></div>}
          <div className="flex justify-between"><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Stop</span><span className="text-[#FF3B30]">${pick.stop_loss?.toFixed(2)}</span></div>
        </div>
      ) : (
        <div className="bg-black/40 border border-white/10 rounded-sm p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Subscribe to see</div>
          <div className="text-[11px] text-zinc-400">Premium · Targets · Stop · Rationale</div>
        </div>
      )}
      {hasAccess && pick.rationale && (
        <div className="mt-4 p-3 bg-black/40 border-l-2 rounded-sm text-[12px] text-zinc-300 leading-relaxed" style={{ borderColor: accent }}>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Rationale</div>
          {pick.rationale}
        </div>
      )}
    </motion.div>
  );
}

function ClosedRow({ pick, product, hasAccess }) {
  const ticker = product === "stocks" ? pick.ticker : pick.underlying;
  const label = product === "stocks" ? null : `$${pick.strike} ${(pick.option_type || "").toUpperCase()} ${pick.expiration}`;
  const pctNum = pick.result_pct;
  const pctColor = pctNum == null ? "text-zinc-500" : pctNum > 0 ? "text-[#22C55E]" : pctNum < 0 ? "text-[#FF3B30]" : "text-zinc-500";
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`closed-row-${pick.pick_id}`}>
      <td className="px-3 py-2 font-mono-d text-xs text-zinc-400">{dateFmt(pick.closed_at)}</td>
      <td className="px-3 py-2 font-bold">{ticker}</td>
      {product === "options" && <td className="px-3 py-2 text-xs text-zinc-400 font-mono-d">{label}</td>}
      <td className="px-3 py-2 font-mono-d text-xs">{hasAccess ? usd(pick.entry_price) : "—"}</td>
      <td className="px-3 py-2 font-mono-d text-xs">{hasAccess ? usd(pick.exit_price) : "—"}</td>
      <td className={`px-3 py-2 text-right font-mono-d text-sm font-bold ${pctColor}`}>{signed(pctNum)}</td>
      <td className="px-3 py-2 text-right"><OutcomeBadge outcome={pick.outcome} /></td>
    </tr>
  );
}

function HistoryGrid({ product, accent }) {
  const [grain, setGrain] = useState("monthly");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/${product}/picks/history?grain=${grain}`).then((r) => setData(r.data)).catch(() => setData({ buckets: [] }));
  }, [product, grain]);

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-2xl">Historical results</h3>
        <div className="inline-flex border border-white/15 rounded-sm overflow-hidden" data-testid={`history-grain-tabs-${product}`}>
          {["daily", "weekly", "monthly"].map((g) => (
            <button key={g} onClick={() => setGrain(g)} data-testid={`history-grain-${g}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${grain === g ? "bg-white text-black" : "text-zinc-400 hover:text-white"} ${g !== "daily" ? "border-l border-white/15" : ""}`}>
              {g}
            </button>
          ))}
        </div>
      </div>
      {!data || data.buckets.length === 0 ? (
        <div className="text-xs text-zinc-500 italic py-6 text-center">No closed picks yet — track record starts as soon as the first {product === "stocks" ? "stock" : "option"} pick closes.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-3 py-2">Bucket</th>
                <th className="text-right px-3 py-2">Picks</th>
                <th className="text-right px-3 py-2">W-L</th>
                <th className="text-right px-3 py-2">Win %</th>
                <th className="text-right px-3 py-2">Avg %</th>
              </tr>
            </thead>
            <tbody>
              {data.buckets.slice().reverse().map((b) => (
                <tr key={b.bucket} className="border-b border-white/5">
                  <td className="px-3 py-2 font-mono-d text-zinc-300">{b.bucket}</td>
                  <td className="px-3 py-2 text-right font-mono-d">{b.picks}</td>
                  <td className="px-3 py-2 text-right font-mono-d text-zinc-300">{b.wins}-{b.losses}</td>
                  <td className="px-3 py-2 text-right font-mono-d font-bold" style={{ color: b.win_rate >= 0.55 ? "#22C55E" : b.win_rate >= 0.4 ? "#F59E0B" : "#FF3B30" }}>{pct(b.win_rate)}</td>
                  <td className="px-3 py-2 text-right font-mono-d font-bold" style={{ color: b.avg_pct > 0 ? "#22C55E" : "#FF3B30" }}>{signed(b.avg_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function MarketsPicks({ product = "stocks" }) {
  const meta = PRODUCT_META[product];
  const { user } = useAuth();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${product}/picks/feed?days=120`);
      setFeed(data);
    } catch {
      setFeed(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [product]);

  const Icon = meta.icon;
  const accent = meta.accent;
  const isLoggedIn = !!user;
  const hasAccess = feed?.has_access;

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-10">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-sm flex items-center justify-center" style={{ background: `${accent}1F`, border: `1px solid ${accent}66` }}>
            <Icon size={22} weight="fill" style={{ color: accent }} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: accent }}>Accurate Monkey · {product === "stocks" ? "Equities" : "Derivatives"}</div>
            <h1 className="font-display text-5xl sm:text-6xl leading-none mt-1">{meta.name.toUpperCase()}</h1>
          </div>
        </div>
        <p className="text-sm text-zinc-400 max-w-3xl leading-relaxed">
          Hand-curated {product === "stocks" ? "equity trades" : "options plays"} with a clear entry, two profit targets, an active stop, and full rationale.
          Every pick is locked and timestamped before delivery — outcomes are recorded against the exact entry price, with no edits after close.
        </p>
      </motion.div>

      {/* Trust strip */}
      <div className="bg-[#121212] border border-[#22C55E]/30 rounded-sm px-5 py-3 mb-8 flex items-center gap-3 flex-wrap">
        <ShieldCheck size={16} weight="fill" className="text-[#22C55E]" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#22C55E] font-bold">Tamper-Evident</span>
        <span className="text-xs text-zinc-400">
          Entry · targets · stop are locked at posting time. Outcomes use the exit price recorded in the immutable ledger.
        </span>
      </div>

      {/* Inline admin entry — only visible to admins */}
      {user?.role === "admin" && (
        <motion.section
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 bg-[#121212] border-2 rounded-sm overflow-hidden"
          style={{ borderColor: `${accent}66` }}
          data-testid="markets-admin-inline-section"
        >
          <div className="px-5 py-3 flex items-center justify-between border-b border-white/10" style={{ background: `${accent}14` }}>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: accent }}>
                <Plus size={14} weight="bold" className="text-black" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: accent }}>Admin · Quick add</div>
                <div className="font-display text-lg leading-tight">Post a {product === "stocks" ? "stock" : "options"} pick</div>
              </div>
            </div>
            <Link
              to="/admin/markets"
              data-testid="markets-admin-fullview-link"
              className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 rounded-sm transition-colors"
            >
              Manage all → /admin/markets
            </Link>
          </div>
          <div className="p-5">
            <NewPickForm product={product} onCreated={load} />
          </div>
        </motion.section>
      )}

      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-3xl">Open positions</h2>
        <button onClick={load} disabled={loading} data-testid={`${product}-refresh`}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40">
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {loading && !feed && <div className="text-zinc-500 font-mono-d py-10 text-center text-sm">Loading…</div>}

      {feed && (
        <>
          {feed.open.length === 0 ? (
            <div className="bg-[#0F0F0F] border border-white/10 rounded-sm px-6 py-10 text-center mb-10" data-testid={`${product}-open-empty`}>
              <div className="text-sm text-zinc-400 mb-1">No open positions right now.</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-600">
                Subscribers get an instant email the moment a new pick is posted.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid={`${product}-open-list`}>
              {feed.open.map((p) => product === "stocks"
                ? <OpenStockCard key={p.pick_id} pick={p} accent={accent} hasAccess={hasAccess} />
                : <OpenOptionCard key={p.pick_id} pick={p} accent={accent} hasAccess={hasAccess} />)}
            </div>
          )}

          {!hasAccess && (
            <div className="bg-[#121212] border rounded-sm p-6 mb-10" style={{ borderColor: `${accent}66` }} data-testid={`${product}-subscribe-cta`}>
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>Subscriber-only</div>
                  <div className="font-display text-2xl mt-1">Unlock entry, targets, stop, and rationale</div>
                  <div className="text-sm text-zinc-400 mt-1 max-w-2xl">
                    Start a 7-day free trial. Cancel anytime. {meta.cta} is $25/week individually or bundled with Sports + Options for $55/week all-access.
                  </div>
                </div>
                <Link to="/pricing" className="px-5 py-2.5 text-xs uppercase tracking-widest font-bold rounded-sm transition-colors text-white" style={{ background: accent }} data-testid={`${product}-subscribe-btn`}>
                  See plans
                </Link>
              </div>
            </div>
          )}

          {/* Aggregated historical results */}
          <HistoryGrid product={product} accent={accent} />

          {/* Closed picks ledger */}
          <h2 className="font-display text-3xl mb-4">Recent closed picks</h2>
          {feed.closed.length === 0 ? (
            <div className="text-zinc-500 italic text-sm py-8 text-center">No closed picks in the last 120 days.</div>
          ) : (
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-x-auto" data-testid={`${product}-closed-table`}>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <tr>
                    <th className="text-left px-3 py-2">Closed</th>
                    <th className="text-left px-3 py-2">{product === "stocks" ? "Ticker" : "Underlying"}</th>
                    {product === "options" && <th className="text-left px-3 py-2">Contract</th>}
                    <th className="text-left px-3 py-2">Entry</th>
                    <th className="text-left px-3 py-2">Exit</th>
                    <th className="text-right px-3 py-2">Result</th>
                    <th className="text-right px-3 py-2">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {feed.closed.map((p) => <ClosedRow key={p.pick_id} pick={p} product={product} hasAccess={hasAccess} />)}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
